import {analyticsLogger} from './analyticsLogger';

/** Where the search box shows a live answer: its pane, or the list (no pane yet). */
export type LiveAnswerPlacement = 'pane' | 'inline';

/**
 * How a live answer's view ended: handed to Ask, left through one of its links,
 * read with neither (the answer was enough), or closed before it came back.
 */
export type LiveAnswerOutcome = 'continued' | 'link' | 'read' | 'before_answer';

const INTERNAL_KINDS: Record<string, string> = {
  people: 'person',
  teams: 'team',
  workstreams: 'workstream',
  news: 'news',
  content: 'knowledge',
};

/**
 * What an answer's link points at, never the link itself: a CompanyOS page by
 * its section, a knowledge article, a Dropbox doc, or anything else outside.
 */
export const linkKind = (href: string) => {
  let url: URL;
  try {
    url = new URL(href, window.location.origin);
  } catch {
    return 'other';
  }
  if (url.origin === window.location.origin) {
    return INTERNAL_KINDS[url.pathname.split('/')[1]] ?? 'other_internal';
  }
  if (/service-?now/i.test(url.hostname)) return 'knowledge';
  if (/(^|\.)dropbox\.com$/i.test(url.hostname)) return 'dropbox';
  return 'external';
};

/** The answer finished (or came from this tab's cache), `answerMs` after it was shown. */
export const logLiveAnswerShown = ({
  placement,
  cached,
  answerMs,
}: {
  placement: LiveAnswerPlacement;
  cached: boolean;
  answerMs: number;
}) => {
  analyticsLogger().logEvent('SEARCH_LIVE_ANSWER_SHOWN', {
    placement,
    cached,
    answer_ms: Math.round(answerMs),
    search_box: 'relaunch',
  });
};

export const logLiveAnswerLinkClicked = ({
  placement,
  href,
  answered,
}: {
  placement: LiveAnswerPlacement;
  href: string;
  answered: boolean;
}) => {
  analyticsLogger().logEvent('SEARCH_LIVE_ANSWER_LINK_CLICKED', {
    placement,
    link_kind: linkKind(href),
    answered,
    search_box: 'relaunch',
  });
};

/**
 * One per live answer that started (or came from the cache), when it leaves
 * the screen: how long it was up, how long its answer was, and what came of it.
 */
export const logLiveAnswerClosed = ({
  placement,
  cached,
  outcome,
  visibleMs,
  answerVisibleMs,
  linkClicks,
}: {
  placement: LiveAnswerPlacement;
  cached: boolean;
  outcome: LiveAnswerOutcome;
  visibleMs: number;
  answerVisibleMs: number;
  linkClicks: number;
}) => {
  analyticsLogger().logEvent('SEARCH_LIVE_ANSWER_CLOSED', {
    placement,
    cached,
    outcome,
    visible_ms: Math.round(visibleMs),
    answer_visible_ms: Math.round(answerVisibleMs),
    link_clicks: linkClicks,
    search_box: 'relaunch',
  });
};
